import { useState, useMemo, useRef } from "react";
import { MapPin, X, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import townZipcodeMapping from "@/data/townZipcodeMapping.json";

export interface LocationSelection {
  type: "town" | "zip";
  displayName: string;
  zips: string[];
}

interface LocationTypeaheadProps {
  value: LocationSelection[];
  onChange: (selections: LocationSelection[]) => void;
  placeholder?: string;
  className?: string;
}

type TownData = {
  zips: string[];
  county: string;
  state: string;
};

const townData = townZipcodeMapping as Record<string, TownData>;

// Build a reverse lookup: ZIP -> town names
const zipToTowns: Record<string, string[]> = {};
Object.entries(townData).forEach(([town, data]) => {
  data.zips.forEach((zip) => {
    if (!zipToTowns[zip]) {
      zipToTowns[zip] = [];
    }
    zipToTowns[zip].push(town);
  });
});

// Get all unique ZIPs
const allZips = [...new Set(Object.values(townData).flatMap((d) => d.zips))].sort();

// Extract a compact label for badge display
const getShortLabel = (sel: LocationSelection): string => {
  if (sel.type === "zip") {
    // "07030 (Hoboken)" → "07030"
    return sel.displayName.split(" (")[0];
  }
  // "Hoboken, NJ - Hudson County" → "Hoboken, NJ"
  return sel.displayName.split(" - ")[0];
};

export function LocationTypeahead({
  value,
  onChange,
  placeholder = "City, town, or ZIP code",
  className = "",
}: LocationTypeaheadProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Check if a selection is already chosen
  const isSelected = (displayName: string) =>
    value.some((s) => s.displayName === displayName);

  // Filter results based on input
  const filteredResults = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    if (!query) return { towns: [], zips: [] };

    const isZipQuery = /^\d{1,5}$/.test(query);

    const towns: { name: string; data: TownData }[] = [];
    const zips: string[] = [];

    if (isZipQuery) {
      allZips.forEach((zip) => {
        if (zip.startsWith(query)) {
          zips.push(zip);
        }
      });
      zips.splice(10);
    }

    Object.entries(townData).forEach(([name, data]) => {
      if (name.toLowerCase().includes(query)) {
        towns.push({ name, data });
      }
    });

    towns.sort((a, b) => {
      const aExact = a.name.toLowerCase() === query;
      const bExact = b.name.toLowerCase() === query;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      return a.name.localeCompare(b.name);
    });

    towns.splice(15);

    return { towns, zips };
  }, [inputValue]);

  const handleSelectTown = (townName: string, data: TownData) => {
    const displayName = `${townName}, ${data.state} - ${data.county} County`;
    if (isSelected(displayName)) {
      // Toggle off
      onChange(value.filter((s) => s.displayName !== displayName));
    } else {
      onChange([...value, { type: "town", displayName, zips: data.zips }]);
    }
    setInputValue("");
    // Keep popover open so user can add more, but clear input
  };

  const handleSelectZip = (zip: string) => {
    const towns = zipToTowns[zip] || [];
    const displayName = towns.length > 0 ? `${zip} (${towns[0]})` : zip;
    if (isSelected(displayName)) {
      onChange(value.filter((s) => s.displayName !== displayName));
    } else {
      onChange([...value, { type: "zip", displayName, zips: [zip] }]);
    }
    setInputValue("");
  };

  const handleRemove = (displayName: string) => {
    onChange(value.filter((s) => s.displayName !== displayName));
    inputRef.current?.focus();
  };

  const hasResults = filteredResults.towns.length > 0 || filteredResults.zips.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className={`relative flex flex-wrap items-center gap-1 min-h-[48px] max-h-[96px] overflow-y-auto pl-9 pr-3 py-1.5 border border-input rounded-md bg-background cursor-text ${className}`}
          onClick={() => inputRef.current?.focus()}
        >
          <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />

          {/* Selected location chips */}
          {value.map((sel) => (
            <Badge
              key={sel.displayName}
              className="flex items-center gap-1 shrink-0 max-w-[180px] bg-accent text-accent-foreground border border-primary/20 text-xs px-2 py-0.5 rounded-full font-medium hover:bg-accent/80 transition-colors"
            >
              <span className="truncate">{getShortLabel(sel)}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(sel.displayName);
                }}
                className="ml-0.5 h-3.5 w-3.5 rounded-full hover:bg-primary/10 inline-flex items-center justify-center transition-colors"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </Badge>
          ))}

          {/* Clear all button */}
          {value.length >= 2 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
                inputRef.current?.focus();
              }}
              className="shrink-0 text-[10px] text-muted-foreground hover:text-foreground transition-colors px-1"
            >
              Clear all
            </button>
          )}

          {/* Always-visible input */}
          <Input
            ref={inputRef}
            type="text"
            placeholder={value.length === 0 ? placeholder : "Add more…"}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (!open && e.target.value) setOpen(true);
            }}
            onFocus={() => inputValue && setOpen(true)}
            className="flex-1 min-w-[100px] border-0 shadow-none focus-visible:ring-0 h-8 px-0 py-0 text-sm"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 bg-popover"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandList>
            {!hasResults && inputValue.length > 0 && (
              <CommandEmpty>No locations found.</CommandEmpty>
            )}
            {filteredResults.zips.length > 0 && (
              <CommandGroup heading="ZIP Codes">
                {filteredResults.zips.map((zip) => {
                  const towns = zipToTowns[zip] || [];
                  const displayName = towns.length > 0 ? `${zip} (${towns[0]})` : zip;
                  const selected = isSelected(displayName);
                  return (
                    <CommandItem
                      key={zip}
                      value={zip}
                      onSelect={() => handleSelectZip(zip)}
                      className="cursor-pointer"
                    >
                      <span className="font-mono">{zip}</span>
                      {towns.length > 0 && (
                        <span className="ml-2 text-muted-foreground text-sm">
                          ({towns.slice(0, 2).join(", ")})
                        </span>
                      )}
                      {selected && <Check className="ml-auto h-4 w-4" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {filteredResults.towns.length > 0 && (
              <CommandGroup heading="Towns & Cities">
                {filteredResults.towns.map(({ name, data }) => {
                  const displayName = `${name}, ${data.state} - ${data.county} County`;
                  const selected = isSelected(displayName);
                  return (
                    <CommandItem
                      key={name}
                      value={name}
                      onSelect={() => handleSelectTown(name, data)}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{name}, {data.state}</span>
                        <span className="text-xs text-muted-foreground">
                          {data.county} County • {data.zips.length} ZIP{data.zips.length > 1 ? "s" : ""}
                        </span>
                      </div>
                      {selected && <Check className="ml-auto h-4 w-4" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default LocationTypeahead;
